import {
	Alert,
	Box,
	Button,
	ColumnLayout,
	Container,
	Header,
	SpaceBetween
} from '@cloudscape-design/components'
import { FC } from 'react'
import { useNewApplicationFormStore } from '../../../../stores/applications/newApplicationFormStore'
import ReadGenericTagsGCP from '../../../ControlledFormInputs/GenericTagsReadGCP'
import ApproverOverview from '../../../ControlledFormInputs/ApproverOverview'
import { buildApplicationTagApprovalPreview } from '../../../../helper/applicationTagApproval'

const getDisplayText = (value: any) => {
	if (Array.isArray(value)) {
		return value.filter(Boolean).join(', ')
	}

	if (value && typeof value === 'object') {
		return value.label || value.value || ''
	}

	return value || ''
}

const getDisplayValue = (value: any) => getDisplayText(value) || '-'

type ReviewField = {
	label: string,
	value: string
}

const ApplicationReview: FC = () => {
	const {
		formValues,
		actions: { setCurrentStep }
	} = useNewApplicationFormStore()


	const applicationTargetFields: Array<ReviewField> = [
		{
			label: 'GCP Project',
			value: formValues.gcpProjectID
		},
		{
			label: 'Infrastructure Type',
			value: formValues.infrastructureType === 'gke' ? "GKE" : "Non-GKE"
		},
		...(formValues.infrastructureType === 'gke'
			? [
					{
						label: 'GKE Workload Cluster',
						value: formValues.gcpProjectID?.resolution?.resolvedCluster?.namePrefix
					}
				]
			: [])
	]

	const applicationDetailsFields: Array<ReviewField> = [
		{ label: 'Application Name', value: formValues.applicationName },
		{ label: 'Application Alias', value: formValues.applicationAlias },
		{
			label: 'Application Viewer(s)',
			value: formValues.applicationViewer
		},
		{
			label: 'Platform Owner',
			value: formValues.platformOwner
		},
		{
			label: 'Platform Owner Email',
			value: formValues.platformOwnerEmail
		},
		{
			label: 'Application Owner Name',
			value: formValues.applicationOwner
		},
		{
			label: 'Application Owner Email',
			value: formValues.applicationOwnerEmail
		},
		{
			label: 'Deployment',
			value: String(formValues.deployment || '').toUpperCase()
		},
		{
			label: 'Application Data Classification',
			value: formValues.dataClassification
		},
		{ label: 'Tier Level', value: formValues.tierLevel },
		{ label: 'Portfolio', value: formValues.portfolio }
	]

	let applicationApprovalPreview: any[] = []
	let approvalPreviewError = ''
	try {
		applicationApprovalPreview = buildApplicationTagApprovalPreview(
			formValues.gcpProjectID?.resolution?.selectedProject?.tags
				?.approvalStatus || [],
			formValues.applicationName
		)
	} catch (error) {
		// The preview throws when a project level approval the application inherits is missing
		// or unapproved. Surface it here as the Resource Tags step does — an empty approval list
		// would make the review page look valid while hiding the blocking approval state.
		approvalPreviewError =
			error instanceof Error
				? error.message
				: 'Selected GCP Project approvals cannot be carried forward.'
	}

	const renderReviewSection = (
		title: string,
		stepIndex: number,
		items: Array<{ label: string; value: any }>
	) => (

		// Template
		<Container
			header={
				<Header
					variant="h3"
					actions={
						<Button onClick={() => setCurrentStep(stepIndex)}>Edit</Button>
					}
				>
					{title}
				</Header>
			}
		>
			<ColumnLayout columns={2} variant="text-grid">
				{items.map((item) => (
					<div key={item.label}>
						<Box variant="awsui-key-label">{item.label}</Box>
						<div>{getDisplayValue(item.value)}</div>
					</div>
				))}
			</ColumnLayout>
		</Container>
	)

	return (
		<SpaceBetween direction="vertical" size="l">
			{renderReviewSection('Application Target', 0, applicationTargetFields)}
			{renderReviewSection('Application Details', 0, applicationDetailsFields )}

			<Container
				header={
					<Header
						variant="h3"
						actions={
							<Button onClick={() => setCurrentStep(1)}>Edit</Button>
						}
					>
						Resource Tags
					</Header>
				}
			>
				<SpaceBetween direction="vertical" size="m">
					{approvalPreviewError && (
						<Alert type="error" header="Resource tag approvals unavailable">
							{approvalPreviewError}
						</Alert>
					)}
					<ReadGenericTagsGCP
						tagsId={formValues.tagsID}
						showApprovalStatus={false}
					/>
					<div>
						<Box variant="awsui-key-label">{"Application Owner"}</Box>
						{formValues.applicationOwner || '-'}
					</div>
					{!approvalPreviewError && (
						<ApproverOverview
							approvalStatus={applicationApprovalPreview}
							isDisabled
						/>
					)}
				</SpaceBetween>
			</Container>
		</SpaceBetween>
	)
}

export default ApplicationReview
